import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../firebaseConfig';
import { listSeasons } from '../../../utils/seasonApi';
import { buildModeRowsFromRoster, sanitizeRowsForSave, validateAssignments } from './helpers';

const MODE_KEYS = ['HP', 'SND', 'CTL'];

const emptyAssignments = {
  HP: [],
  SND: [],
  CTL: [],
};

export const usePlayerRoles = ({ teamId, uid, selectedSeason }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seasonDocId, setSeasonDocId] = useState(null);
  const [activePlayers, setActivePlayers] = useState([]);
  const [playerImages, setPlayerImages] = useState({});
  const [assignmentsByMode, setAssignmentsByMode] = useState(emptyAssignments);

  useEffect(() => {
    let active = true;

    const loadTeamConfig = async () => {
      if (!teamId) {
        if (active) {
          setActivePlayers([]);
          setPlayerImages({});
        }
        return;
      }

      const configSnap = await getDoc(doc(db, `teams/${teamId}/configurations/stats`));
      if (!active) return;

      const config = configSnap.exists() ? configSnap.data() : {};
      const players = Array.isArray(config?.players) ? config.players : [];
      const imagesMap = {};
      const roster = players
        .filter((player) => player?.isActive !== false)
        .map((player) => player?.ign || player?.name || '')
        .filter(Boolean);

      players.forEach((player) => {
        const name = player?.ign || player?.name;
        if (name && player?.imageUrl) imagesMap[name] = player.imageUrl;
      });

      setActivePlayers(roster);
      setPlayerImages(imagesMap);
    };

    loadTeamConfig().catch((err) => {
      console.error('Failed to load player role config', err);
    });

    return () => {
      active = false;
    };
  }, [teamId]);

  useEffect(() => {
    let active = true;

    const resolveSeason = async () => {
      if (!teamId || !selectedSeason) {
        setSeasonDocId(null);
        return;
      }

      const seasons = await listSeasons(teamId);
      if (!active) return;

      const match =
        seasons.find((season) => String(season.year || season.id) === String(selectedSeason)) ||
        seasons.find((season) => String(season.id) === String(selectedSeason));

      setSeasonDocId(match ? String(match.id) : String(selectedSeason));
    };

    resolveSeason().catch((err) => {
      console.error('Failed to resolve player role season', err);
      if (!active) return;
      setSeasonDocId(selectedSeason ? String(selectedSeason) : null);
    });

    return () => {
      active = false;
    };
  }, [teamId, selectedSeason]);

  useEffect(() => {
    let active = true;

    const loadAssignments = async () => {
      if (!teamId || !seasonDocId) {
        setAssignmentsByMode(emptyAssignments);
        setLoading(false);
        return;
      }

      setLoading(true);
      const snapshot = await getDoc(doc(db, `teams/${teamId}/seasons/${seasonDocId}/database/PlayerRoles`));
      if (!active) return;

      const payload = snapshot.exists() ? snapshot.data() : {};
      setAssignmentsByMode({
        HP: buildModeRowsFromRoster('HP', activePlayers, payload?.HP || []),
        SND: buildModeRowsFromRoster('SND', activePlayers, payload?.SND || []),
        CTL: buildModeRowsFromRoster('CTL', activePlayers, payload?.CTL || []),
      });
      setLoading(false);
    };

    loadAssignments().catch((err) => {
      console.error('Failed to load player assignments', err);
      if (!active) return;
      setAssignmentsByMode({
        HP: buildModeRowsFromRoster('HP', activePlayers),
        SND: buildModeRowsFromRoster('SND', activePlayers),
        CTL: buildModeRowsFromRoster('CTL', activePlayers),
      });
      setLoading(false);
      toast.error('Failed to load player assignments');
    });

    return () => {
      active = false;
    };
  }, [teamId, seasonDocId, activePlayers]);

  const validations = useMemo(() => ({
    HP: validateAssignments(assignmentsByMode.HP),
    SND: validateAssignments(assignmentsByMode.SND),
    CTL: validateAssignments(assignmentsByMode.CTL),
  }), [assignmentsByMode]);

  const updateAssignment = (mode, rowIndex, field, value) => {
    setAssignmentsByMode((prev) => ({
      ...prev,
      [mode]: prev[mode].map((row, index) => (
        index === rowIndex
          ? { ...row, [field]: value }
          : row
      )),
    }));
  };

  const resetMode = (mode) => {
    setAssignmentsByMode((prev) => ({
      ...prev,
      [mode]: buildModeRowsFromRoster(mode, activePlayers, prev[mode]),
    }));
  };

  const clearMode = (mode) => {
    setAssignmentsByMode((prev) => ({
      ...prev,
      [mode]: buildModeRowsFromRoster(mode, activePlayers, []),
    }));
  };

  const saveAssignments = async () => {
    if (!teamId || !seasonDocId) return;

    setSaving(true);
    try {
      await setDoc(
        doc(db, `teams/${teamId}/seasons/${seasonDocId}/database/PlayerRoles`),
        {
          HP: sanitizeRowsForSave('HP', assignmentsByMode.HP),
          SND: sanitizeRowsForSave('SND', assignmentsByMode.SND),
          CTL: sanitizeRowsForSave('CTL', assignmentsByMode.CTL),
          seasonId: String(selectedSeason || seasonDocId),
          updatedAt: serverTimestamp(),
          updatedBy: uid || null,
        },
        { merge: true }
      );

      const invalidModes = MODE_KEYS.filter((mode) => !validations[mode].isValid);
      if (invalidModes.length) {
        toast.warn(`Assignments saved, but ${invalidModes.join(', ')} still have rule issues.`);
      } else {
        toast.success('Player assignments saved.');
      }
    } catch (err) {
      console.error('Failed to save player assignments', err);
      toast.error('Failed to save player assignments');
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    saving,
    activePlayers,
    playerImages,
    assignmentsByMode,
    validations,
    updateAssignment,
    resetMode,
    clearMode,
    saveAssignments,
  };
};

export default usePlayerRoles;
